import { Button, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select, TextField, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { GATEWAY_URL, generatePassword } from '../../Helper';
import { useApi } from '../../hooks/useApi';
import { DataGrid } from '@mui/x-data-grid';
import { IconButton } from '@mui/material';
import { Add, ArrowBack, DeleteForever, Edit, LockReset } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useErrorDialog } from '../../providers/ErrorDialogProvider';

function AddEditDialog({ selectedAccount, postAccount, setSelectedAccount }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(false)
    if (selectedAccount) {
      setEmail(selectedAccount.username || "");
      setEmail(selectedAccount.role || "");
    }
  }, [selectedAccount]);

  return (
    <Dialog open={Boolean(selectedAccount)} onClose={() => setSelectedAccount(null)}>
      <DialogTitle>Benutzer hinzufügen</DialogTitle>
      <DialogContent style={{ width: "450px" }}>
        {!loading ?
          <>
            <TextField
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ marginTop: "10px", width: "100%" }}
            />
            <Select value={role} onChange={(e) => {setRole(e.target.value)}} style={{marginTop: "10px", width: "100%"}}>
              <MenuItem value={'user'}>Anwender</MenuItem>
              <MenuItem value={'admin'}>Verwalter</MenuItem>
            </Select>
          </> : 
          <div style={{display: "flex", flexDirection: "column", gap: 16, justifySelf: "center"}}>
            <CircularProgress style={{alignSelf: "center"}}/>
            <Typography>Sende Email</Typography>
          </div>
        }
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" disabled={loading} onClick={() => setSelectedAccount(null)}>
          ABBRECHEN
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            setLoading(true)
            postAccount(email, role);
          }}
          disabled={!email || loading}
        >
          HINZUFÜGEN
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ResetPasswordDialog({ accoutResetPassword, postResetPassword, setAccoutResetPassword }) {
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    setLoading(false)
  }, [accoutResetPassword]);

  return(
    <Dialog open={accoutResetPassword != null}>
      <DialogTitle>Passwort zurücksetzen?</DialogTitle>
      <DialogContent>
      {!loading ?
          <>
            <Typography>Möchten Sie das Passwort für "{accoutResetPassword?.username}" zurücksetzen?</Typography>
          </> : 
          <div style={{display: "flex", flexDirection: "column", gap: 16, justifySelf: "center"}}>
            <CircularProgress style={{alignSelf: "center"}}/>
            <Typography>Sende Email</Typography>
          </div>
        }
      </DialogContent>
      <DialogActions>
        <Button type="button" variant='outlined' disabled={loading} onClick={(e) => {
            setAccoutResetPassword(null);
          }}
        >
          NEIN
        </Button>
        <Button type="button" variant='contained' disabled={loading} onClick={(e) => {
            setLoading(true)
            postResetPassword(accoutResetPassword)
          }}
        >
          JA
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function CompanyDetailView() {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedCompany = location.state.selectedCompany;

  const { showMsg, showError } = useErrorDialog();

  const [items, setItems] = useState([]);

  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accoutResetPassword, setAccoutResetPassword] = useState(null);
  const [accountToDelete, setAccountToDelete] = useState(null); 

  const api = useApi();

  function getUsers() {
    api.customRequest(GATEWAY_URL + '/getUsers', {method: "GET"}, async (resp) => {
      let items = await resp?.json();
      if (resp.ok && items) {
        let filtered = items.filter((item) => item.companyId != null && item.companyId == selectedCompany._id);
        setItems(filtered);
      }
    });
  }

  function postAccount(email, role) {
    let newUser = {
      ...selectedAccount,
      companyId: selectedCompany._id,
      role: role,
      username: email,
      password: generatePassword()
    }

    const requestOptions = {
      method: "POST",
      body: JSON.stringify(newUser),
      redirect: "follow"
    };
    api.customRequest(GATEWAY_URL + '/addUser', requestOptions, async (resp) => {
      getUsers();
      setSelectedAccount(null);
      if(!resp.ok) {
        let error = await resp.json();
        showError(error?.message ?? "Unknown");
      }
    });
  }

  function deleteAccount(accountToDelete) {
    const requestOptions = {
      method: "DELETE",
      redirect: "follow"
    };
    api.customRequest(GATEWAY_URL + '/deleteUser/' + accountToDelete?._id, requestOptions, async (resp) => {
      getUsers();
      setAccountToDelete(null);
      if(!resp.ok) {
        let error = await resp.json();
        showError(error?.message ?? "Unknown");
      }
    });
  }

  function postResetPassword(user) {
    const requestOptions = {
      method: "POST",
      body: JSON.stringify({userId: user._id}),
      redirect: "follow"
    };
    api.customRequest(GATEWAY_URL + '/resetPassword', requestOptions, async (resp) => {
      getUsers();
      setAccoutResetPassword(null);
      if(!resp.ok) {
        let error = await resp.json();
        showError(error?.message ?? "Unknown");
      }
    });
  }

  const roleMapping = {
    admin: 'Verwalter',
    user: 'Benutzer',
  };

  const columns = [
    { 
      field: 'username',
      headerName: 'Email',
      flex: 1
    },
    { 
      field: 'role',
      headerName: 'Rolle',
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (params) => roleMapping[params] || params
    },
    { 
      field: 'deviceId',
      headerName: 'Geräte ID / Angemeldet',
      flex: 1,
      align: 'right',
      headerAlign: 'right'
    },
    { 
      field: 'actions', 
      headerName: 'Funktionen', 
      flex: 0.5,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <>
          <Tooltip title="Passwort zurücksetzen">
            <IconButton aria-label="edit" onClick={(e) => {
              e.preventDefault()
              setAccoutResetPassword(params.row);
            }}>
              <LockReset />
            </IconButton>
          </Tooltip>
          <Tooltip title="Benutzer löschen">
            <IconButton aria-label="delete" onClick={(e) => {
              e.preventDefault()
              setAccountToDelete(params.row);
            }}>
              <DeleteForever />
            </IconButton>
          </Tooltip>
        </>
      ) 
    }
  ];

  useEffect(() => {
    getUsers();
  }, []);

  const disableCreate = items.length >= selectedCompany.maxAccounts

  return (
    <div className="content">
      <Container style={{display: "flex", flexDirection: "column", gap: 16}}>
        <div style={{display: "flex", alignItems: "center", marginTop: '50px', justifyContent: "space-between"}}>
          <IconButton onClick={() => navigate("/teams")}><ArrowBack/></IconButton>
          <Typography fontSize={30}>DETAILS - {selectedCompany?.companyName}</Typography>
          <Tooltip title={disableCreate ? "Max. Anzahl erreicht" : "Benutzer hinzufügen"}>
            <span>
              <IconButton disabled={disableCreate} onClick={() => setSelectedAccount({})}><Add/></IconButton>
            </span>
          </Tooltip>
        </div>
        
        {/* Render list view with all Codes */}
        <div style={{ height: 400, width: '100%' }}>
          <DataGrid
            rows={items.map((item, index) => ({ id: index, ...item }))}
            rowSelection={true}
            columns={columns}
            pageSize={5}
            disableSelectionOnClick
            sx={{
              '& .MuiDataGrid-cell': {
                cursor: 'pointer', // Add pointer cursor on hover
              }
            }}
          />
        </div>

        <AddEditDialog
          selectedAccount={selectedAccount}
          setSelectedAccount={setSelectedAccount}
          postAccount={postAccount}
        />

        <ResetPasswordDialog
          accoutResetPassword={accoutResetPassword}
          setAccoutResetPassword={setAccoutResetPassword}
          postResetPassword={postResetPassword}
        />

        <Dialog open={accountToDelete != null}>
          <DialogTitle>Benutzer löschen?</DialogTitle>
          <DialogContent>
            <p>Möchten Sie "{accountToDelete?.username}" wirklich löschen?</p>
          </DialogContent>
          <DialogActions>
            <Button type="button" variant='outlined' onClick={(e) => {
                setAccountToDelete(null);
              }}
            >
              NEIN
            </Button>
            <Button type="button" variant='contained' onClick={(e) => {
                deleteAccount(accountToDelete);
                setAccountToDelete(null);
              }}
            >
              JA
            </Button>
          </DialogActions>
        </Dialog>

      </Container>
    </div>
  );
}

export default CompanyDetailView;
